import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { 
  Shield, 
  FileImage, 
  Camera, 
  Smile, 
  CheckCircle, 
  ChevronRight,
  AlertTriangle,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import { DocumentOCR } from '../../components/identity/DocumentOCR';
import { LivenessChallenge } from '../../components/identity/LivenessChallenge';
import { FaceMatch } from '../../components/identity/FaceMatch';
import { AntiFraudAnalysis } from '../../components/identity/AntiFraudAnalysis';
import api from '../../lib/api';

type VerificationStep = 'intro' | 'document' | 'liveness' | 'face-match' | 'anti-fraud' | 'complete' | 'failed';

interface SessionData {
  id: string;
  status: string;
  currentStep: string;
  extractedData?: {
    fullName?: string;
    cpf?: string;
    documentNumber?: string;
    dateOfBirth?: string;
  };
  livenessScore?: number;
  faceMatchScore?: number;
  fraudRiskScore?: number;
}

const STEPS = [
  { id: 'document', label: 'Documento', icon: FileImage },
  { id: 'liveness', label: 'Vivacidade', icon: Smile },
  { id: 'face-match', label: 'Comparação', icon: Camera },
  { id: 'anti-fraud', label: 'Verificação', icon: Shield },
];

export function IdentityVerification() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<VerificationStep>('intro');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [documentImage, setDocumentImage] = useState<string | null>(null);
  const [selfieImage, setSelfieImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Create verification session
  const createSessionMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<SessionData>('/identity/sessions');
      return response.data;
    },
    onSuccess: (data) => {
      setSessionId(data.id);
      setCurrentStep('document');
    },
    onError: (err) => {
      setError('Erro ao criar sessão de verificação');
      console.error(err);
    },
  });

  // Update session with document data
  const updateDocumentMutation = useMutation({
    mutationFn: async (data: { ocrResult: unknown; imageData: string }) => {
      const response = await api.post(`/identity/sessions/${sessionId}/document`, {
        extractedData: data.ocrResult,
        documentImageUrl: data.imageData,
      });
      return response.data;
    },
    onSuccess: () => {
      setCurrentStep('liveness');
    },
    onError: (err) => {
      setError('Erro ao processar documento');
      console.error(err);
    },
  });

  // Update session with liveness data
  const updateLivenessMutation = useMutation({
    mutationFn: async (data: { results: unknown[]; score: number }) => {
      const response = await api.post(`/identity/sessions/${sessionId}/liveness`, {
        challenges: data.results,
        overallScore: data.score,
      });
      return response.data;
    },
    onSuccess: () => {
      setCurrentStep('face-match');
    },
    onError: (err) => {
      setError('Erro ao processar verificação de vivacidade');
      console.error(err);
    },
  });

  // Update session with face match data
  const updateFaceMatchMutation = useMutation({
    mutationFn: async (data: { result: Record<string, unknown>; selfieImage: string }) => {
      const response = await api.post(`/identity/sessions/${sessionId}/face-match`, {
        ...data.result,
        selfieImageUrl: data.selfieImage,
      });
      return response.data;
    },
    onSuccess: () => {
      setCurrentStep('anti-fraud');
    },
    onError: (err) => {
      setError('Erro ao processar comparação facial');
      console.error(err);
    },
  });

  // Complete session
  const completeSessionMutation = useMutation({
    mutationFn: async (fraudResult: unknown) => {
      const response = await api.post(`/identity/sessions/${sessionId}/complete`, {
        fraudAnalysis: fraudResult,
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.status === 'COMPLETED') {
        setCurrentStep('complete');
      } else {
        setCurrentStep('failed');
      }
    },
    onError: (err) => {
      setError('Erro ao finalizar verificação');
      console.error(err);
    },
  });

  // Get current session status
  const { data: sessionData } = useQuery({
    queryKey: ['identity-session', sessionId],
    queryFn: async () => {
      const response = await api.get<SessionData>(`/identity/sessions/${sessionId}`);
      return response.data;
    },
    enabled: !!sessionId,
    refetchInterval: currentStep === 'complete' || currentStep === 'failed' ? false : 5000,
  });

  const handleStartVerification = () => {
    createSessionMutation.mutate();
  };

  const handleDocumentComplete = useCallback((ocrResult: unknown, imageData: string) => {
    setDocumentImage(imageData);
    updateDocumentMutation.mutate({ ocrResult, imageData });
  }, [updateDocumentMutation]);

  const handleLivenessComplete = useCallback((results: unknown[], score: number) => {
    if (score < 0.8) {
      setError('Verificação de vivacidade falhou. Por favor, tente novamente.');
      return;
    }
    updateLivenessMutation.mutate({ results, score });
  }, [updateLivenessMutation]);

  const handleFaceMatchComplete = useCallback((result: { matched: boolean; requiresReview: boolean; similarity: number }, selfie: string) => {
    setSelfieImage(selfie);
    if (!result.matched && !result.requiresReview) {
      setError('Comparação facial falhou. As faces não correspondem.');
      return;
    }
    updateFaceMatchMutation.mutate({ result, selfieImage: selfie });
  }, [updateFaceMatchMutation]);

  const handleAntiFraudComplete = useCallback((result: { blocked: boolean }) => {
    if (result.blocked) {
      setError('Verificação bloqueada por suspeita de fraude.');
      setCurrentStep('failed');
      return;
    }
    completeSessionMutation.mutate(result);
  }, [completeSessionMutation]);

  const getStepIndex = () => {
    const stepMap: Record<string, number> = {
      document: 0,
      liveness: 1,
      'face-match': 2,
      'anti-fraud': 3,
    };
    return stepMap[currentStep] ?? -1;
  };

  const isLoading = 
    createSessionMutation.isPending ||
    updateDocumentMutation.isPending ||
    updateLivenessMutation.isPending ||
    updateFaceMatchMutation.isPending ||
    completeSessionMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Verificação de Identidade</h1>
          <p className="text-gray-600">Complete as etapas abaixo para verificar sua identidade</p>
        </div>

        {/* Progress Steps */}
        {currentStep !== 'intro' && currentStep !== 'complete' && currentStep !== 'failed' && (
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const currentIndex = getStepIndex();
                const isActive = index === currentIndex;
                const isCompleted = index < currentIndex;

                return (
                  <div key={step.id} className="flex items-center">
                    <div className={`flex flex-col items-center ${index > 0 ? 'ml-4' : ''}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isCompleted 
                          ? 'bg-green-500 text-white' 
                          : isActive 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <Icon className="w-5 h-5" />
                        )}
                      </div>
                      <span className={`text-xs mt-1 ${isActive ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                        {step.label}
                      </span>
                    </div>
                    {index < STEPS.length - 1 && (
                      <ChevronRight className={`w-5 h-5 mx-2 ${
                        index < currentIndex ? 'text-green-500' : 'text-gray-300'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg flex items-center gap-4">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              <span>Processando...</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          {/* Intro Step */}
          {currentStep === 'intro' && (
            <div className="text-center py-8">
              <Shield className="w-20 h-20 text-blue-600 mx-auto mb-6" />
              <h2 className="text-xl font-semibold mb-4">Verificação de Identidade</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Para sua segurança, precisamos verificar sua identidade. 
                O processo leva apenas alguns minutos e inclui:
              </p>
              <ul className="text-left max-w-sm mx-auto mb-8 space-y-3">
                <li className="flex items-center gap-3">
                  <FileImage className="w-5 h-5 text-blue-500" />
                  <span>Foto do documento (RG, CNH ou Passaporte)</span>
                </li>
                <li className="flex items-center gap-3">
                  <Smile className="w-5 h-5 text-blue-500" />
                  <span>Verificação de vivacidade</span>
                </li>
                <li className="flex items-center gap-3">
                  <Camera className="w-5 h-5 text-blue-500" />
                  <span>Selfie para comparação facial</span>
                </li>
              </ul>
              <button
                onClick={handleStartVerification}
                disabled={createSessionMutation.isPending}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createSessionMutation.isPending ? 'Iniciando...' : 'Iniciar Verificação'}
              </button>
            </div>
          )}

          {/* Document Step */}
          {currentStep === 'document' && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Envie seu documento</h2>
              <DocumentOCR
                onComplete={handleDocumentComplete}
                onError={(err) => setError(err)}
              />
            </div>
          )}

          {/* Liveness Step */}
          {currentStep === 'liveness' && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Verificação de Vivacidade</h2>
              <p className="text-gray-600 mb-6">
                Siga as instruções na tela para provar que você é uma pessoa real.
              </p>
              <LivenessChallenge
                onComplete={handleLivenessComplete}
                onError={(err) => setError(err)}
              />
            </div>
          )}

          {/* Face Match Step */}
          {currentStep === 'face-match' && documentImage && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Comparação Facial</h2>
              <p className="text-gray-600 mb-6">
                Tire uma selfie para compararmos com a foto do seu documento.
              </p>
              <FaceMatch
                documentImage={documentImage}
                onComplete={(result) => handleFaceMatchComplete(result, selfieImage || '')}
                onError={(err) => setError(err)}
              />
            </div>
          )}

          {/* Anti-Fraud Step */}
          {currentStep === 'anti-fraud' && selfieImage && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Análise de Segurança</h2>
              <p className="text-gray-600 mb-6">
                Estamos verificando a autenticidade das suas imagens.
              </p>
              <AntiFraudAnalysis
                imageData={selfieImage}
                onComplete={handleAntiFraudComplete}
                onError={(err) => setError(err)}
              />
            </div>
          )}

          {/* Complete Step */}
          {currentStep === 'complete' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-green-800 mb-4">
                Verificação Concluída!
              </h2>
              <p className="text-gray-600 mb-6">
                Sua identidade foi verificada com sucesso. 
                Agora você pode acessar todos os recursos da plataforma.
              </p>
              <button
                onClick={() => navigate('/banking')}
                className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Continuar
              </button>
            </div>
          )}

          {/* Failed Step */}
          {currentStep === 'failed' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-red-800 mb-4">
                Verificação Não Concluída
              </h2>
              <p className="text-gray-600 mb-6">
                Não foi possível verificar sua identidade. 
                Por favor, entre em contato com o suporte para assistência.
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => {
                    setCurrentStep('intro');
                    setSessionId(null);
                    setDocumentImage(null);
                    setSelfieImage(null);
                    setError(null);
                  }}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Tentar Novamente
                </button>
                <button
                  onClick={() => navigate('/banking/support')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Contatar Suporte
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Session Info (Debug) */}
        {sessionData && process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-4 bg-gray-100 rounded-lg text-xs">
            <p>Session ID: {sessionData.id}</p>
            <p>Status: {sessionData.status}</p>
            <p>Step: {sessionData.currentStep}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default IdentityVerification;
